import { formatInTimeZone, fromZonedTime, toZonedTime } from 'date-fns-tz';

/**
 * UTC on the wire, facility time on screen, no timezone suffix — §9.10.
 *
 * Every instant that reaches a user goes through this module. There is one
 * `formatFacility()` and no exceptions: the moment a component reaches for
 * `toLocaleString()` the rendering depends on where the operator's laptop
 * thinks it is, and a basement in Doha read from a laptop still set to Belgrade
 * is a support call nobody can reproduce.
 */

/** The facility time zone. UTC+3, no DST — Qatar has never observed it. */
export const FACILITY_TIME_ZONE = 'Asia/Qatar';

/** Fixed offset, stated so a reader does not have to trust the tz database. */
export const FACILITY_UTC_OFFSET_HOURS = 3;

/**
 * `14 Oct 2026, 09:12`. Two-digit day so a column of timestamps under
 * `font-variant-numeric: tabular-nums` actually lines up.
 */
export const FACILITY_DATETIME_FORMAT = 'dd MMM yyyy, HH:mm';
export const FACILITY_DATE_FORMAT = 'dd MMM yyyy';
export const FACILITY_TIME_FORMAT = 'HH:mm';

/** Anything that can name an instant. Strings are parsed as ISO 8601. */
export type Instant = Date | string | number;

export function toDate(instant: Instant): Date {
  if (instant instanceof Date) return instant;
  if (typeof instant === 'number') return new Date(instant);
  return new Date(instant);
}

export function isValidInstant(instant: Instant): boolean {
  return !Number.isNaN(toDate(instant).getTime());
}

/**
 * The same instant, expressed as a `Date` whose *local* fields read as facility
 * time. Use it when a date library needs calendar fields (which hour bucket a
 * reading falls in, which facility day a report covers) — never for display,
 * because its `toISOString()` is deliberately misleading. Display goes through
 * `formatFacility()`.
 */
export function toFacilityTime(instant: Instant): Date {
  return toZonedTime(toDate(instant), FACILITY_TIME_ZONE);
}

/**
 * The inverse: calendar fields the user typed in facility time, back to the
 * true UTC instant that goes on the wire.
 */
export function fromFacilityTime(facilityLocal: Date | string): Date {
  return fromZonedTime(facilityLocal, FACILITY_TIME_ZONE);
}

/** `14 Oct 2026, 09:12` — no timezone suffix, ever. */
export function formatFacility(
  instant: Instant,
  pattern: string = FACILITY_DATETIME_FORMAT,
): string {
  return formatInTimeZone(toDate(instant), FACILITY_TIME_ZONE, pattern);
}

/** `14 Oct 2026` */
export function formatFacilityDate(instant: Instant): string {
  return formatFacility(instant, FACILITY_DATE_FORMAT);
}

/** `09:12` — the `as at 08:41` suffix the counter strip grows under staleness. */
export function formatFacilityTime(instant: Instant): string {
  return formatFacility(instant, FACILITY_TIME_FORMAT);
}

/** `2026-10-14` in facility time — the value a date input round-trips. */
export function facilityDateKey(instant: Instant): string {
  return formatFacility(instant, 'yyyy-MM-dd');
}

/** `2026-10` in facility time — the `occupancy_monthly` month parameter. */
export function facilityMonthKey(instant: Instant): string {
  return formatFacility(instant, 'yyyy-MM');
}

/** UTC ISO 8601 with offset. What goes on the wire, always. */
export function toWire(instant: Instant): string {
  return toDate(instant).toISOString();
}

/** Whole seconds between two instants, `to` defaulting to now. */
export function secondsBetween(from: Instant, to: Instant = Date.now()): number {
  return Math.round((toDate(to).getTime() - toDate(from).getTime()) / 1000);
}

/** Age of an instant in seconds. Negative for an instant in the future. */
export function ageSeconds(instant: Instant, now: Instant = Date.now()): number {
  return secondsBetween(instant, now);
}
